import React, { createContext, useState, useMemo, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isIpadPwa, isIphonePwa, reloadDocument, statusbarNeedsNewDocument } from '../utils/ipadPwa';
import { getPhoneModes } from "../hooks/usePhoneNavigation";
import { TabletPortraitProvider } from '../hooks/useTabletPortrait';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider
} from '@material-ui/core/styles';
import Portal from '@material-ui/core/Portal';

const AUTH_ROUTE = /(^|\/)(login|signup|forgetpsw|termos|privacidade|exclusao-dados)(\/|$)/i;

// The same empty, paintless bar the logged-in layout uses: it only tells the phone which color to use.
const authStatusbarStyle = (color) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: 11,
  backgroundColor: color,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  zIndex: 2147483647,
  pointerEvents: "none",
});

// Create the color mode context
export const ColorModeContext = createContext({
  toggleColorMode: () => { },
  mode: 'light'
});

// Main theme provider component
export const ThemeProvider = ({ children }) => {
  // Get the user's preferred color scheme from localStorage or use system preference
  const getInitialMode = () => {
    // Verifica query param ?theme= (passado pelo site neozapp.com.br)
    try {
      const params = new URLSearchParams(window.location.search);
      const themeParam = params.get('theme');
      if (themeParam === 'dark' || themeParam === 'light') {
        localStorage.setItem('themeMode', themeParam);
        const url = new URL(window.location.href);
        url.searchParams.delete('theme');
        window.history.replaceState({}, '', url.toString());
        return themeParam;
      }
    } catch (_) {}

    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      return savedMode;
    }
    // Check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  };

  const [mode, setMode] = useState(getInitialMode);

  // Color mode toggler function
  const location = useLocation();
  const pathname = location?.pathname || "";

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        // The statusbar only changes with a new document. The screen holds its theme until
        // then, so the page and the statusbar change together.
        if (statusbarNeedsNewDocument()) {
          localStorage.setItem('themeMode', mode === 'light' ? 'dark' : 'light');
          reloadDocument();
          return;
        }
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', newMode);

          // Dispara evento personalizado para coordenar com ThemeLoader
          window.dispatchEvent(new CustomEvent('theme-change', {
            detail: { newMode, previousMode: prevMode }
          }));

          return newMode;
        });
      },
      mode,
    }),
    [mode],
  );

  // Generate theme based on current mode with CSS variables integration
  const theme = useMemo(
    () => {
      // Apply CSS variables to document
      const root = document.documentElement;
      root.setAttribute('data-theme', mode);
      document.body.setAttribute('data-theme', mode);

      // Handle classes for Safari theme-color overrides
      root.classList.remove('theme-light', 'theme-dark');
      root.classList.add(`theme-${mode}`);

      const pColor = '#14A45C';
      const pColorDark = '#0F7A3E';
      const pColorLight = '#4CAF50';
      // One outline for every surface that floats over the page.
      const contour = mode === 'light'
        ? '1px solid rgba(0, 0, 0, 0.08)'
        : '1px solid rgba(255, 255, 255, 0.12)';

      return createTheme({
        palette: {
          type: mode,
          mode: mode,
          primary: {
            main: pColor,
            light: '#4CAF50',
            dark: '#0F7A3E',
            contrastText: '#ffffff',
          },
          secondary: {
            main: mode === 'light' ? '#ff5722' : '#ff784e',
            light: '#ff8a65',
            dark: '#e64a19',
            contrastText: '#ffffff',
          },
          background: {
            default: mode === 'light' ? '#f5f5f7' : '#0a0a0a',
            paper: mode === 'light' ? '#ffffff' : '#1a1a1a',
            auth: mode === 'light' ? '#14A45C' : '#1c1c1c',
          },
          text: {
            primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
            secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
          },
          error: {
            main: '#f44336',
            light: '#e57373',
            dark: '#d32f2f',
          },
          warning: {
            main: '#ff9800',
            light: '#ffb74d',
            dark: '#f57c00',
          },
          info: {
            main: '#2196f3',
            light: '#64b5f6',
            dark: '#1976d2',
          },
          success: {
            main: '#4caf50',
            light: '#81c784',
            dark: '#388e3c',
          },
          divider: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.1)',
          // Custom colors with direct values for MUI compatibility
          dark: {
            main: mode === 'light' ? '#333333' : '#ffffff',
          },
          light: {
            main: mode === 'light' ? '#f3f3f3' : '#252525',
          },
          fancyBackground: mode === 'light' ? '#f5f5f7' : '#0a0a0a',
          barraSuperior: mode === 'light' ? '#14A45C' : '#1a1a1a',
          textPrimary: mode === 'light' ? '#14A45C' : '#ffffff',
          borderPrimary: mode === 'light' ? '#14A45C' : '#ffffff',
          tabHeaderBackground: mode === 'light' ? '#eee' : '#252525',
          optionsBackground: mode === 'light' ? '#fafafa' : '#1a1a1a',
          options: mode === 'light' ? '#fafafa' : '#252525',
          fontecor: mode === 'light' ? '#128c7e' : '#fff',
          bordabox: mode === 'light' ? '#eee' : '#1a1a1a',
          newmessagebox: mode === 'light' ? '#eee' : '#1a1a1a',
          inputdigita: mode === 'light' ? '#fff' : '#252525',
          contactdrawer: mode === 'light' ? '#fff' : '#1a1a1a',
          announcements: mode === 'light' ? '#ededed' : '#1a1a1a',
          login: mode === 'light' ? '#fff' : '#1c1c1c',
          announcementspopover: mode === 'light' ? '#fff' : '#252525',
          chatlist: mode === 'light' ? '#eee' : '#252525',
          boxlist: mode === 'light' ? '#ededed' : '#252525',
          boxchatlist: mode === 'light' ? '#ededed' : '#1a1a1a',
          total: mode === 'light' ? '#fff' : '#1a1a1a',
          messageIcons: mode === 'light' ? 'grey' : '#f3f3f3',
          inputBackground: mode === 'light' ? '#ffffff' : '#1a1a1a',
          boxticket: mode === 'light' ? '#eee' : '#252525',
          campaigntab: mode === 'light' ? '#ededed' : '#252525',
          mediainput: mode === 'light' ? '#ededed' : '#1c1c1c',
        },
        typography: {
          fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          h1: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.2,
          },
          h2: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.3,
          },
          h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.3,
          },
          h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          h5: {
            fontSize: '1.1rem',
            fontWeight: 600,
            lineHeight: 1.4,
          },
          h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
          },
          body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
          },
          body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
          },
          button: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
        shape: {
          borderRadius: 8,
        },
        overrides: {
          MuiButton: {
            root: {
              borderRadius: 8,
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 24px',
              transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)', // Spring-like
              letterSpacing: '0.3px',
              height: '32.5px',
              minHeight: '32.5px',
            },
            contained: {
              boxShadow: mode === 'light'
                ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                : '0 4px 16px rgba(0, 0, 0, 0.5)',
              '&:hover': {
                boxShadow: mode === 'light'
                  ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                  : '0 8px 24px rgba(0, 0, 0, 0.7)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            },
            containedPrimary: {
              boxShadow: mode === 'light'
                ? '0 2px 5px rgba(0, 0, 0, 0.08)'
                : '0 3px 10px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                boxShadow: mode === 'light'
                  ? '0 4px 10px rgba(0, 0, 0, 0.12)'
                  : '0 5px 14px rgba(0, 0, 0, 0.45)',
              },
            },
          },
          MuiPaper: {
            root: {
              transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
              backgroundImage: 'none',
            },
            rounded: {
              borderRadius: 12, // Keeping 12px as requested
            },
            elevation1: {
              boxShadow: mode === 'light'
                ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                : '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.2)',
              border: mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.04)'
                : '1px solid rgba(255, 255, 255, 0.05)',
            },
            elevation2: {
              boxShadow: mode === 'light'
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
              border: mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.04)'
                : '1px solid rgba(255, 255, 255, 0.05)',
            },
          },
          MuiAppBar: {
            colorPrimary: {
              backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.96)' : 'rgba(28, 28, 28, 0.96)',
              backdropFilter: 'blur(8px)',
              borderBottom: mode === 'light' ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)',
              boxShadow: 'none', // Flat floating style
            },
          },
          MuiDrawer: {
            paper: {
              backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.96)' : 'rgba(26, 26, 26, 0.96)',
              backdropFilter: 'blur(8px)',
            },
          },
          MuiTableCell: {
            root: {
              borderBottom: mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            },
            head: {
              fontWeight: 600,
              backgroundColor: mode === 'light'
                ? '#f5f5f7'
                : '#262626',
            },
          },
          MuiTabs: {
            root: {
              // Linear-like: keep tabs on the same surface (no extra background strip)
              backgroundColor: "transparent",
              // Keep subtle separation from content without adding a solid strip
              boxShadow: mode === "light"
                ? "0 1px 0 rgba(0, 0, 0, 0.06), 0 10px 20px -20px rgba(0, 0, 0, 0.25), 0 -10px 20px -20px rgba(0, 0, 0, 0.12)"
                : "0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 24px -24px rgba(0, 0, 0, 0.85), 0 -12px 24px -24px rgba(0, 0, 0, 0.55)",
              position: "relative",
              zIndex: 1,
            },
            indicator: {
              height: 3,
            },
          },
          MuiTab: {
            root: {
              textTransform: 'none',
              fontWeight: 500,
              '&$selected': {
                fontWeight: 600,
              },
            },
          },
          MuiListItem: {
            root: {
              '&$selected': {
                backgroundColor: mode === 'light'
                  ? 'rgba(104, 46, 226, 0.1)'
                  : 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: mode === 'light'
                    ? 'rgba(104, 46, 226, 0.15)'
                    : 'rgba(255, 255, 255, 0.12)',
                },
              },
              '&:hover': {
                backgroundColor: mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.05)',
              },
            },
          },
          MuiChip: {
            root: {
              borderRadius: 16,
            },
          },
          // Padronização de superfícies com valores diretos
          MuiCard: {
            root: {
              backgroundColor: mode === 'light' ? '#ffffff' : '#1a1a1a',
            },
          },
          MuiDialog: {
            paper: {
              backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.96)' : 'rgba(26, 26, 26, 0.96)',
              backdropFilter: 'blur(8px)',
              boxShadow: mode === 'light'
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: contour,
            },
            paperScrollPaper: {
              display: 'flex',
              maxHeight: 'calc(100% - 48px)',
              flexDirection: 'column',
            },
          },
          // Dialog sections: remove default "divider lines" (DialogContent `dividers`)
          // to keep a cleaner, Linear-like modal surface.
          MuiDialogTitle: {
            root: {
              padding: "20px 24px 16px",
              borderBottom: 'none',
            },
          },
          MuiDialogContent: {
            root: {
              padding: "8px 24px 16px",
            },
            dividers: {
              borderTop: 'none',
              borderBottom: 'none',
            },
          },
          MuiDialogActions: {
            root: {
              padding: "16px 24px 24px",
              borderTop: 'none',
            },
          },
          MuiBackdrop: {
            root: {
              // On the light theme the blur lightens the page instead of darkening it.
              backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              '&.MuiBackdrop-invisible': {
                backgroundColor: 'transparent',
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            },
          },
          // Helper text strategy:
          // - Desktop (pointer fine): hide helper text to keep layouts visually clean
          // - Mobile (pointer coarse): keep helper text visible for discoverability
          MuiFormHelperText: {
            root: {
              "@media (hover: hover) and (pointer: fine)": {
                display: "none",
              },
            },
          },
          MuiMenu: {
            paper: {
              backgroundColor: mode === 'light' ? '#ffffff' : '#1a1a1a',
            },
          },
          // Menu and Select lists render inside a Popover, so they inherit this outline.
          MuiPopover: {
            paper: {
              backgroundColor: mode === 'light' ? '#ffffff' : '#1a1a1a',
              border: contour,
            },
          },
          MuiAutocomplete: {
            paper: {
              border: contour,
            },
          },
          MuiTooltip: {
            tooltip: {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              color: mode === 'light' ? '#ffffff' : '#000000',
            },
          },
        },
        scrollbarStyles: {
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.15)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.25)',
          },
          '&::-webkit-scrollbar-track': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
        },
        scrollbarStylesSoft: {
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.2)',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        },
        // ============================================
        // NeoUI Design System - Unified Token Namespace
        // ============================================
        neoUI: {
          // Floating effects (depth, shadows, transforms)
          floating: {
            card: {
              boxShadow: mode === 'light'
                ? '0 20px 60px -10px rgba(0, 0, 0, 0.15), 0 8px 24px -4px rgba(0, 0, 0, 0.1)'
                : '0 20px 60px -10px rgba(0, 0, 0, 0.6), 0 8px 24px -4px rgba(0, 0, 0, 0.4)',
              hoverShadow: mode === 'light'
                ? '0 28px 70px -10px rgba(0, 0, 0, 0.18), 0 12px 30px -4px rgba(0, 0, 0, 0.12)'
                : '0 28px 70px -10px rgba(0, 0, 0, 0.7), 0 12px 30px -4px rgba(0, 0, 0, 0.5)',
              border: mode === 'light'
                ? '1px solid rgba(0, 0, 0, 0.04)'
                : '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: 20,
            },
            button: {
              boxShadow: mode === 'light'
                ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                : '0 4px 16px rgba(0, 0, 0, 0.5)',
              hoverShadow: mode === 'light'
                ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                : '0 8px 24px rgba(0, 0, 0, 0.7)',
              warning: {
                boxShadow: mode === 'light'
                  ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                  : '0 4px 16px rgba(0, 0, 0, 0.5)',
                hoverShadow: mode === 'light'
                  ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                  : '0 8px 24px rgba(0, 0, 0, 0.7)',
              },
              error: {
                boxShadow: mode === 'light'
                  ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                  : '0 4px 16px rgba(0, 0, 0, 0.5)',
                hoverShadow: mode === 'light'
                  ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                  : '0 8px 24px rgba(0, 0, 0, 0.7)',
              },
              floating: {
                transform: 'scale(1.005)',
                boxShadow: mode === 'light'
                  ? '0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                  : '0 8px 24px rgba(0, 0, 0, 0.7)',
              },
            },
            input: {
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.04)',
              focusShadow: mode === 'light'
                ? '0 0 0 3px rgba(20, 164, 92, 0.15)'
                : '0 0 0 3px rgba(20, 164, 92, 0.3)',
            },
            container: {
              boxShadow: mode === 'light'
                ? '0px 4px 15px rgba(0, 0, 0, 0.06)'
                : '0px 4px 15px rgba(0, 0, 0, 0.4)',
              hoverShadow: mode === 'light'
                ? '0px 4px 15px rgba(0, 0, 0, 0.08)'
                : '0px 6px 20px rgba(0, 0, 0, 0.5)',
              transform: 'scale(1.005)',
            },
          },
          // Button presets
          buttons: {
            action: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 33,
                height: 33,
                padding: "0 12px",
                backgroundColor: pColor,
                color: "#ffffff",
                borderRadius: 8,
                border: "none",
                boxShadow: mode === 'light'
                  ? '0 2px 5px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)'
                  : '0 3px 10px rgba(0, 0, 0, 0.3)',
                "&:hover": {
                  backgroundColor: pColorDark,
                  boxShadow: mode === 'light'
                    ? '0 4px 10px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)'
                    : '0 5px 14px rgba(0, 0, 0, 0.45)',
                }
              },
              busy: {
                backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                boxShadow: "none",
                opacity: 1,
                cursor: "not-allowed",
                pointerEvents: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                  boxShadow: "none",
                  transform: "none",
                }
              },
              busyOutline: {
                backgroundColor: mode === "light" ? "rgba(195, 201, 207, 0.18)" : "rgba(75, 82, 88, 0.22)",
                color: mode === "light" ? "rgba(0, 0, 0, 0.48)" : "rgba(255, 255, 255, 0.7)",
                borderColor: mode === "light" ? "#bcc3ca" : "#555d64",
                boxShadow: "none",
                opacity: 1,
                cursor: "not-allowed",
                pointerEvents: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: mode === "light" ? "rgba(195, 201, 207, 0.18)" : "rgba(75, 82, 88, 0.22)",
                  color: mode === "light" ? "rgba(0, 0, 0, 0.48)" : "rgba(255, 255, 255, 0.7)",
                  borderColor: mode === "light" ? "#bcc3ca" : "#555d64",
                  boxShadow: "none",
                  transform: "none",
                }
              },
              busyCancel: {
                backgroundColor: mode === "light" ? "rgba(195, 201, 207, 0.18)" : "rgba(75, 82, 88, 0.22)",
                color: mode === "light" ? "rgba(0, 0, 0, 0.48)" : "rgba(255, 255, 255, 0.7)",
                borderColor: mode === "light" ? "#bcc3ca" : "#555d64",
                boxShadow: "none",
                opacity: 1,
                cursor: "not-allowed",
                pointerEvents: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: mode === "light" ? "rgba(195, 201, 207, 0.18)" : "rgba(75, 82, 88, 0.22)",
                  color: mode === "light" ? "rgba(0, 0, 0, 0.48)" : "rgba(255, 255, 255, 0.7)",
                  borderColor: mode === "light" ? "#bcc3ca" : "#555d64",
                  boxShadow: "none",
                  transform: "none",
                }
              },
              busyWarning: {
                backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                boxShadow: "none",
                opacity: 1,
                cursor: "not-allowed",
                pointerEvents: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                  boxShadow: "none",
                  transform: "none",
                }
              },
              busyError: {
                backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                boxShadow: "none",
                opacity: 1,
                cursor: "not-allowed",
                pointerEvents: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  borderColor: mode === "light" ? "#c3c9cf" : "#4b5258",
                  color: mode === "light" ? "rgba(0, 0, 0, 0.52)" : "rgba(255, 255, 255, 0.78)",
                  boxShadow: "none",
                  transform: "none",
                }
              },
              warning: {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: "32.5px",
                height: "32.5px",
                padding: "0 10px",
                backgroundColor: '#fbc02d', // Kanban Amarelo Base
                color: "#ffffff",
                borderRadius: 8,
                border: "none",
                boxShadow: mode === 'light'
                  ? '0 2px 5px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)'
                  : '0 3px 10px rgba(0, 0, 0, 0.3)',
                "&:hover": {
                  backgroundColor: '#f9a825', // Kanban Amarelo Hover
                  boxShadow: mode === 'light'
                    ? '0 4px 10px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)'
                    : '0 5px 14px rgba(0, 0, 0, 0.45)',
                }
              },
              error: {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: "32.5px",
                height: "32.5px",
                padding: "0 10px",
                backgroundColor: '#d32f2f', // Kanban Vermelho Base
                color: "#ffffff",
                borderRadius: 8,
                border: "none",
                boxShadow: mode === 'light'
                  ? '0 2px 5px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)'
                  : '0 3px 10px rgba(0, 0, 0, 0.3)',
                "&:hover": {
                  backgroundColor: '#b71c1c', // Kanban Vermelho Hover
                  boxShadow: mode === 'light'
                    ? '0 4px 10px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)'
                    : '0 5px 14px rgba(0, 0, 0, 0.45)',
                }
              },
              outline: {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: "32.5px",
                height: "32.5px",
                padding: "0 10px",
                backgroundColor: "transparent",
                color: pColor,
                borderRadius: 8,
                border: `1px solid ${pColor}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: mode === 'light' ? 'rgba(20, 164, 92, 0.08)' : 'rgba(20, 164, 92, 0.15)',
                  borderColor: pColor,
                  color: pColor,
                }
              },
              cancel: {
                root: {
                  color: "#ff784e",
                  borderColor: "#ff784e",
                  border: "1px solid #ff784e",
                  backgroundColor: "transparent",
                  minHeight: "32.5px",
                  height: "32.5px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0 10px",
                  borderRadius: 8,
                  "&:hover": {
                    backgroundColor: "rgba(255, 120, 78, 0.08)",
                    borderColor: "#ff784e",
                  },
                },
                solid: {
                  color: "#ffffff",
                  backgroundColor: "#ff784e",
                  minHeight: "32.5px",
                  height: "32.5px",
                  border: "none",
                  borderRadius: 8,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0 10px",
                  boxShadow: mode === 'light'
                    ? '0 2px 5px rgba(0, 0, 0, 0.08)'
                    : '0 3px 10px rgba(0, 0, 0, 0.3)',
                  "&:hover": {
                    backgroundColor: "#e64a19",
                    boxShadow: mode === 'light'
                      ? '0 4px 10px rgba(0, 0, 0, 0.12)'
                      : '0 5px 14px rgba(0, 0, 0, 0.45)',
                  },
                }
              },
            },

          },
          // Visual effects
          effects: {
            glow: {
              main: mode === 'light'
                ? '0px 2px 12px rgba(0, 0, 0, 0.06)'
                : '0px 4px 15px rgba(0, 0, 0, 0.4)',
              button: mode === 'light'
                ? '0 3px 10px rgba(0, 0, 0, 0.15)'
                : '0 4px 12px rgba(0, 0, 0, 0.4)',
            },
            contour: {
              main: contour,
            },
          },
          // Scrollbar styles
          scrollbar: {
            default: {
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.25)',
              },
              '&::-webkit-scrollbar-track': {
                background: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
              },
            },
            soft: {
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: mode === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: mode === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.2)',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
            },
          },
        },
        // MUI Shadows (required by MUI)
        shadows: mode === 'light'
          ? [
            "none",
            '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // 1
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // 2
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // 3
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // 4
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // 5
            '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 6
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            ...Array(16).fill('0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)')
          ]
          : [
            "none",
            '0 1px 2px 0 rgba(0, 0, 0, 0.3)', // 1
            '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)', // 2
            '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.2)', // 3
            '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)', // 4
            '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)', // 5
            '0 25px 50px -12px rgba(0, 0, 0, 0.5)', // 6
            ...Array(18).fill('0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)')
          ],
        // Add the mode property to the theme
        mode,
      });
    },
    [mode],
  );

  useLayoutEffect(() => {
    const updatePwaMode = () => {
      document.documentElement.toggleAttribute('data-ipad-pwa', isIpadPwa());
      document.documentElement.toggleAttribute('data-iphone-pwa', isIphonePwa());
    };
    const queries = ['(display-mode: standalone)', '(display-mode: fullscreen)']
      .map(query => window.matchMedia ? window.matchMedia(query) : null).filter(Boolean);
    updatePwaMode();
    queries.forEach(query => {
      if (query.addEventListener) query.addEventListener('change', updatePwaMode);
      else if (query.addListener) query.addListener(updatePwaMode);
    });
    window.addEventListener('pageshow', updatePwaMode);
    return () => {
      queries.forEach(query => {
        if (query.removeEventListener) query.removeEventListener('change', updatePwaMode);
        else if (query.removeListener) query.removeListener(updatePwaMode);
      });
      window.removeEventListener('pageshow', updatePwaMode);
    };
  }, []);

  const lastChromeColor = useRef(null);

  useLayoutEffect(() => {
    const setOrCreateMeta = (selector, attributes) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        document.head.appendChild(meta);
      }

      Object.entries(attributes).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });

      return meta;
    };

    const isAuthRoute = AUTH_ROUTE.test(pathname);

    const inAppColor = mode === 'light' ? '#ffffff' : '#1c1c1c';
    const authColor = theme?.palette?.background?.auth
      || (mode === 'light' ? '#14A45C' : '#1c1c1c');
    const browserChromeColor = isAuthRoute ? authColor : inAppColor;
    const colorScheme = mode === "dark" ? "dark light" : "light dark";

    if (isAuthRoute) {
      document.documentElement.classList.add('auth-page');
    } else {
      document.documentElement.classList.remove('auth-page');
    }

    document.documentElement.style.backgroundColor = browserChromeColor;
    document.documentElement.style.setProperty('--ipad-statusbar-color', browserChromeColor);

    setOrCreateMeta('meta[name="theme-color"][data-app-theme="dynamic"]', {
      name: "theme-color",
      content: browserChromeColor,
      "data-app-theme": "dynamic"
    });

    setOrCreateMeta('meta[name="color-scheme"]', {
      name: "color-scheme",
      content: colorScheme
    });

    setOrCreateMeta('meta[name="msapplication-navbutton-color"]', {
      name: "msapplication-navbutton-color",
      content: browserChromeColor
    });

    setOrCreateMeta('meta[name="msapplication-TileColor"]', {
      name: "msapplication-TileColor",
      content: browserChromeColor
    });

    // An installed iPad or iPhone only rereads its statusbar's color if the top bar disappears or is
    // replaced, and that leaves the system blurring the page's top until a reload. A new
    // document gets both right, so a route that changes the color (logout, session end) starts one.
    const previousColor = lastChromeColor.current;
    lastChromeColor.current = browserChromeColor;
    if (previousColor && previousColor !== browserChromeColor && statusbarNeedsNewDocument()) reloadDocument();
  }, [pathname, mode, theme]);

  // Logged in, the layout supplies this surface. Without it the phone blurs the login's top edge.
  const phoneAuthPage = AUTH_ROUTE.test(pathname) && typeof window !== "undefined" && getPhoneModes(window).isPhoneNavigation;

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        {phoneAuthPage && <Portal><div style={authStatusbarStyle(mode === 'light' ? (theme?.palette?.background?.auth || '#14A45C') : '#1c1c1c')} data-mobile-statusbar aria-hidden="true" /></Portal>}
        <TabletPortraitProvider>{children}</TabletPortraitProvider>
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ColorModeContext;
